import {
  Search,
  Briefcase,
  CreditCard,
  Users,
  CalendarDays,
  TrendingUp,
  LucideIcon,
  ArrowRight,
  Clock,
  Zap,
  Activity
} from "lucide-react";
import { Link } from "react-router-dom";
import { useDashboardMetrics } from "../hooks/useLeadCapture";
import { useBookings } from "../hooks/useBooking";
import { useBookingSettings } from "../hooks/useBookingSettings";
import { useJobs } from "../hooks/useJobBoard";
import { BackendInvoiceStatus, billingApi, Invoice } from "../api/billingApi";
import { useEffect, useState } from "react";
import { BookingStatus } from "../types/booking";
import { PlatformAddOn } from "../types/subscription";
import { useAuth } from "../context/AuthContext";

type HomeModule = {
  name: string;
  description: string;
  icon: LucideIcon;
  path: string;
  addOn?: PlatformAddOn;
};

const modules: HomeModule[] = [
  {
    name: "Diagnostics",
    description: "AI Website, Marketing, and Business Health tools.",
    icon: Search,
    path: "/diagnostics/overview",
  },
  {
    name: "Job Board",
    description: "Post jobs, manage applicants, and track recruitment.",
    icon: Briefcase,
    path: "/job-board/dashboard",
    addOn: PlatformAddOn.JobBoard,
  },
  {
    name: "Booking & Scheduling",
    description: "Schedule consultations and manage your calendar.",
    icon: CalendarDays,
    path: "/booking",
    addOn: PlatformAddOn.BookingAndScheduling,
  },
  {
    name: "Forms & Leads",
    description:
      "Create public forms, collect submissions, and track engagement.",
    icon: Users,
    path: "/forms",
    addOn: PlatformAddOn.Forms,
  },
  {
    name: "Billing & Proposals",
    description: "Create invoices, proposals, and manage payments.",
    icon: CreditCard,
    path: "/invoices",
    addOn: PlatformAddOn.BillingAndProposals,
  },
];
// const modules = [
//   {
//     name: "Diagnostics",
//     description: "AI Website, Marketing, and Business Health tools.",
//     icon: Search,
//     path: "/diagnostics/overview",
//   },
//   // {
//   //   name: "Website Platform",
//   //   description: "Manage your website builder and platform essentials.",
//   //   icon: Layers,
//   //   path: "/foundation",
//   // },
//   {
//     name: "Job Board",
//     description: "Post jobs, manage applicants, and track recruitment.",
//     icon: Briefcase,
//     path: "/job-board/overview",
//   },
//   {
//     name: "Booking & Scheduling",
//     description: "Schedule consultations and manage your calendar.",
//     icon: CalendarDays,
//     path: "/booking",
//   },
//   {
//     name: "Forms & Leads",
//     description:
//       "Create public forms, collect submissions, and track engagement.",
//     icon: Users,
//     path: "/forms",
//   },
//   {
//     name: "Billing & Proposals",
//     description: "Create invoices, proposals, and manage payments.",
//     icon: CreditCard,
//     path: "/billing",
//   },
// ];
// KPI Card Component
function KPICard({
  icon: Icon,
  label,
  value,
  trend,
}: {
  icon: LucideIcon;
  label: string;
  value: string | number;
  trend: string;
}) {
  return (
    <div className="bg-white rounded-lg border border-strivre-lightnavy p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-sm font-medium text-strivre-mutedtext">{label}</p>
          <p className="text-3xl font-bold text-strivre-navy mt-2">
            {value}
          </p>
        </div>
        <div className="p-2 bg-strivre-lightburgundy rounded-lg">
          <Icon className="w-6 h-6 text-strivre-burgundy" />
        </div>
      </div>
      <p className="text-xs text-strivre-mutedtext">{trend}</p>
    </div>
  );
}

// Activity Item Component
function ActivityItem({
  icon: Icon,
  description,
  timestamp,
  path,
}: {
  icon: LucideIcon;
  description: string;
  timestamp: Date;
  path?: string;
}) {
  const content = (
    <>
      <div className="p-2 bg-strivre-lightburgundy rounded-lg flex-shrink-0">
        <Icon className="w-5 h-5 text-strivre-burgundy" />
      </div>

      <div className="flex-1 min-w-0 text-left">
        <p className="text-sm font-medium text-strivre-darktext">
          {description}
        </p>

        <p className="text-xs text-strivre-mutedtext mt-1">
          {formatRelativeTime(timestamp)}
        </p>
      </div>

      {path && (
        <ArrowRight className="w-4 h-4 text-strivre-mutedtext flex-shrink-0 mt-1" />
      )}
    </>
  );

  if (path) {
    return (
      <Link
        to={path}
        className="flex items-start gap-4 pb-4 border-b border-strivre-lightnavy last:border-b-0 last:pb-0 group"
      >
        {content}
      </Link>
    );
  }

  return (
    <div className="flex items-start gap-4 pb-4 border-b border-strivre-lightnavy last:border-b-0 last:pb-0">
      {content}
    </div>
  );
}

// Schedule Item Component
function ScheduleItem({
  time,
  title,
  client,
}: {
  time: string;
  title: string;
  client: string;
}) {
  return (
    <div className="flex items-start gap-4 pb-4 border-b border-strivre-lightnavy last:border-b-0 last:pb-0">
      <div className="flex-shrink-0">
        <span className="text-sm font-bold text-strivre-burgundy">{time}</span>
      </div>
      <div className="flex-1 min-w-0 text-left">
        <p className="text-sm font-medium text-strivre-darktext">{title}</p>
        <p className="text-xs text-strivre-mutedtext">{client}</p>
      </div>
      {/* <ArrowRight className="w-4 h-4 text-strivre-mutedtext flex-shrink-0 mt-0.5" /> */}
    </div>
  );
}

// Module Card Component
function ModuleCard({
  icon: Icon,
  name,
  description,
  path,
}: {
  icon: LucideIcon;
  name: string;
  description: string;
  path: string;
}) {
  return (
    <Link
      to={path}
      className="bg-white rounded-lg border border-strivre-lightnavy p-6 hover:shadow-md hover:border-strivre-burgundy transition-all duration-300 group"
    >
      <div className="mb-4">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-strivre-lightburgundy group-hover:bg-strivre-burgundy group-hover:text-white transition-all">
          <Icon className="w-6 h-6 text-strivre-burgundy group-hover:text-white" />
        </div>
      </div>
      <h3 className="text-base font-semibold text-strivre-darktext mb-1">
        {name}
      </h3>
      <p className="text-sm text-strivre-mutedtext">{description}</p>
    </Link>
  );
}

interface DashboardActivity {
  id: string;
  description: string;
  timestamp: Date;
  icon: LucideIcon;
  path?: string;
}

const formatRelativeTime = (date: Date) => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  if (diffMs < 0) {
    return "Just now";
  }

  const diffMinutes = Math.floor(
    diffMs / (1000 * 60)
  );

  const diffHours = Math.floor(
    diffMs / (1000 * 60 * 60)
  );

  const diffDays = Math.floor(
    diffMs / (1000 * 60 * 60 * 24)
  );

  if (diffMinutes < 1) {
    return "Just now";
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} ${
      diffMinutes === 1 ? "minute" : "minutes"
    } ago`;
  }

  if (diffHours < 24) {
    return `${diffHours} ${
      diffHours === 1 ? "hour" : "hours"
    } ago`;
  }

  if (diffDays < 7) {
    return `${diffDays} ${
      diffDays === 1 ? "day" : "days"
    } ago`;
  }

  return date.toLocaleDateString();
};

export default function Home() {
  const { hasAddOn, isAdmin } = useAuth();
  const hasForms = hasAddOn(PlatformAddOn.Forms);
  const hasBooking = hasAddOn(PlatformAddOn.BookingAndScheduling);
  const hasJobBoard = hasAddOn(PlatformAddOn.JobBoard);
  const hasBilling = hasAddOn(PlatformAddOn.BillingAndProposals);

  const visibleModules = modules.filter(
    (module) => {
      if (module.path.startsWith("/diagnostics")) {
        return isAdmin;
      }

      return (
        module.addOn !== undefined &&
        hasAddOn(module.addOn)
      );
    }
  );

  const { data: formsMetrics } = useDashboardMetrics(hasForms);
  const { data: bookings = [] } = useBookings(hasBooking);
  const { data: bookingSettings } = useBookingSettings(hasBooking);
  const bookingTimezone = bookingSettings?.timezone ?? 'UTC';
  const { data: jobs = [] } = useJobs(hasJobBoard);

  const submissionsByForm =
    formsMetrics?.submissionsByForm ?? [];

  const maxSubmissions = Math.max(
    ...submissionsByForm.map(
      (item) => item.count
    ),
    1
  );

  const todayInBookingTimezone =
    new Intl.DateTimeFormat('en-CA', {
      timeZone: bookingTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());

  const todaysBookings = bookings.filter(
    (booking) => {
      const bookingDate =
        new Intl.DateTimeFormat('en-CA', {
          timeZone: bookingTimezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(
          new Date(booking.bookingDate)
        );

      return bookingDate === todayInBookingTimezone &&
        booking.status !== BookingStatus.Cancelled;
    }
  );

  const todaysSchedule = [...todaysBookings].sort(
    (a, b) =>
      new Date(a.bookingDate).getTime() -
      new Date(b.bookingDate).getTime()
  );

  const openPositions = jobs.filter(
    (job) => job.isActive
  );

  const [invoices, setInvoices] = useState<Invoice[]>([]);

  useEffect(() => {
    if (!hasBilling) {
      setInvoices([]);
      return;
    }

    const loadInvoices = async () => {
      try {
        const data =
          await billingApi.getInvoices();

        setInvoices(data);
      } catch {
        setInvoices([]);
      }
    };

    loadInvoices();
  }, [hasBilling]);

  const totalPaidRevenue = invoices
  .filter(
    (invoice) =>
      invoice.status === BackendInvoiceStatus.Paid
  )
  .reduce(
    (sum, invoice) => sum + invoice.totalAmount,
    0
  );

  const overdueInvoices = invoices.filter(
    (invoice) =>
      invoice.status === BackendInvoiceStatus.Overdue
  );

  const pendingBookings = bookings.filter(
    (booking) =>
      booking.status === BookingStatus.Pending
  );

  const activities: DashboardActivity[] = [];

  if (hasForms) {
    formsMetrics?.recentSubmissions.forEach(
      (submission) => {
        activities.push({
          id: `form-${submission.id}`,
          description:
            `New submission received - ${submission.formName}`,
          timestamp: new Date(
            submission.submittedDate
          ),
          icon: Users,
          path: `/forms/${submission.formId}/submissions`,
        });
      }
    );
  }

  if (hasBooking) {
    bookings.forEach((booking) => {
      activities.push({
        id: `booking-${booking.id}`,
        description:
          `New booking - ${booking.serviceName} ` +
          `for ${booking.customerFirstName} ${booking.customerLastName}`,
        timestamp: new Date(
          booking.createdDate
        ),
        icon: CalendarDays,
        path: `/booking/bookings/${booking.id}`,
      });
    });
  }

  if (hasBilling) {
    invoices.forEach((invoice) => {
      activities.push({
        id: `invoice-${invoice.id}`,
        description:
          `Invoice ${invoice.invoiceNumber} issued`,
        timestamp: new Date(
          invoice.issueDate
        ),
        icon: CreditCard,
        path: "/invoices",
      });
    });
  }

  if (hasJobBoard) {
    jobs.forEach((job) => {
      if (!job.publishedDate) {
        return;
      }

      activities.push({
        id: `job-${job.jobId}`,
        description:
          `Job posted - ${job.title}`,
        timestamp: new Date(
          job.publishedDate
        ),
        icon: Briefcase,
        path: "/job-board/overview",
      });
    });
  }

  const recentActivities = activities
  .filter(
    (activity) =>
      !Number.isNaN(
        activity.timestamp.getTime()
      )
  )
  .sort(
    (a, b) =>
      b.timestamp.getTime() -
      a.timestamp.getTime()
  )
  .slice(0, 5);

  const kpiCount = [
  hasForms,
  hasBooking,
  hasJobBoard,
  hasBilling,
].filter(Boolean).length;

const kpiGridClass =
  kpiCount >= 4
    ? "lg:grid-cols-4"
    : kpiCount === 3
      ? "lg:grid-cols-3"
      : kpiCount === 2
        ? "lg:grid-cols-2"
        : "lg:grid-cols-1";

const moduleCount = visibleModules.length;

const moduleGridClass =
  moduleCount >= 3
    ? "lg:grid-cols-3"
    : moduleCount === 2
      ? "lg:grid-cols-2"
      : "lg:grid-cols-1";

const hasActivityModules =
  hasForms ||
  hasBooking ||
  hasJobBoard ||
  hasBilling;

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-full mx-auto p-6 md:p-8 lg:p-10">
        {/* Welcome Banner */}
        <div className="mb-10 animate-fade-in">
          <div className="rounded-lg bg-gradient-to-r from-strivre-navy to-strivre-navy/90 p-8 md:p-12 text-white shadow-lg text-center">
            <h1 className="text-3xl md:text-4xl font-bold mb-2 text-white">
              Let’s Get Started
            </h1>
            <p className="text-strivre-lightnavy text-base md:text-lg mb-6">
              <strong>
                Welcome to your Strivre Services dashboard.
              </strong>
              <br />
              Access your enabled tools, monitor recent activity, and manage your business from one connected platform.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              {isAdmin && (
                <Link
                  to="/diagnostics/overview"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-strivre-navy font-semibold rounded-lg hover:bg-strivre-softgrey transition-colors"
                >
                  <Zap className="w-4 h-4" />
                  Run Diagnostic
                </Link>
              )}
              {hasJobBoard && (
                <Link
                  to="/job-board/dashboard"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-strivre-navy font-semibold rounded-lg hover:bg-strivre-softgrey transition-colors"
                >
                  <Briefcase className="w-4 h-4" />
                  View Jobs
                </Link>
              )}
              {hasForms && (
                <Link
                  to="/forms"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-strivre-navy font-semibold rounded-lg hover:bg-strivre-softgrey transition-colors"
                >
                  <Users className="w-4 h-4" />
                  Manage Forms
                </Link>
              )}
              {hasBooking && (
                <Link
                  to="/booking/bookings"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-strivre-navy font-semibold rounded-lg hover:bg-strivre-softgrey transition-colors"
                >
                  <CalendarDays className="w-4 h-4" />
                  View Bookings
                </Link>
              )}
              {hasBilling && (
                <Link
                  to="/invoices"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-strivre-navy font-semibold rounded-lg hover:bg-strivre-softgrey transition-colors"
                >
                  <CreditCard className="w-4 h-4" />
                  View Invoices
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        {kpiCount > 0 && (
          <div className="mb-10">
            <h2 className="mb-6 text-xl font-bold text-strivre-navy">
              Key Metrics
            </h2>

            <div className={`grid grid-cols-1 gap-6 md:grid-cols-2 ${kpiGridClass}`}>
              {hasForms && (
                <KPICard
                  icon={Users}
                  label="New Leads"
                  value={
                    formsMetrics
                      ? formsMetrics.submissionsToday
                      : "—"
                  }
                  trend="Form submissions received today"
                />
              )}

              {hasBooking && (
                <KPICard
                  icon={CalendarDays}
                  label="Today's Bookings"
                  value={todaysBookings.length}
                  trend={
                    todaysBookings.length === 0
                      ? "No bookings scheduled today"
                      : todaysBookings.length === 1
                        ? "1 appointment scheduled"
                        : `${todaysBookings.length} appointments scheduled`
                  }
                />
              )}

              {hasJobBoard && (
                <KPICard
                  icon={Briefcase}
                  label="Open Positions"
                  value={openPositions.length}
                  trend={
                    openPositions.length === 0
                      ? "No positions currently open"
                      : openPositions.length === 1
                        ? "1 position currently open"
                        : `${openPositions.length} positions currently open`
                  }
                />
              )}

              {hasBilling && (
                <KPICard
                  icon={CreditCard}
                  label="Paid Revenue"
                  value={totalPaidRevenue.toLocaleString(
                    undefined,
                    {
                      style: "currency",
                      currency: "USD",
                    }
                  )}
                  trend="Total revenue from paid invoices"
                />
              )}
            </div>
          </div>
        )}

        {/* Main Content Grid */}
        {hasActivityModules && (
          <div className="mb-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Recent Activity */}
            <div
              className={`h-full rounded-lg border border-strivre-lightnavy bg-white p-6 ${
                !hasBooking ? "lg:col-span-2" : ""
              }`}
            >
              <div className="mb-6 flex items-center gap-2">
                <Activity className="h-5 w-5 text-strivre-burgundy" />

                <h2 className="text-lg font-bold text-strivre-navy">
                  Recent Activity
                </h2>
              </div>

              <div className="space-y-4">
                {recentActivities.length === 0 ? (
                  <div className="py-8 text-center">
                    <Activity
                      size={32}
                      className="mx-auto mb-3 text-strivre-lightnavy"
                    />

                    <p className="text-sm font-medium text-strivre-mutedtext">
                      No recent activity yet.
                    </p>
                  </div>
                ) : (
                  recentActivities.map((activity) => (
                    <ActivityItem
                      key={activity.id}
                      icon={activity.icon}
                      description={activity.description}
                      timestamp={activity.timestamp}
                      path={activity.path}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Today's Schedule */}
            {hasBooking && (
              <div className="h-full rounded-lg border border-strivre-lightnavy bg-white p-6">
                <div className="mb-6 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-strivre-burgundy" />

                    <h2 className="text-lg font-bold text-strivre-navy">
                      Today's Schedule
                    </h2>
                  </div>

                  <span className="rounded-full border border-strivre-lightnavy bg-strivre-softgrey px-3 py-1 text-[11px] font-semibold text-strivre-mutedtext">
                    {bookingTimezone}
                  </span>
                </div>

                <div className="space-y-4">
                  {todaysSchedule.length === 0 ? (
                    <div className="py-6 text-center">
                      <CalendarDays
                        size={32}
                        className="mx-auto mb-3 text-strivre-lightnavy"
                      />

                      <p className="text-sm font-medium text-strivre-mutedtext">
                        No bookings scheduled today.
                      </p>
                    </div>
                  ) : (
                    todaysSchedule.map((booking) => (
                      <ScheduleItem
                        key={booking.id}
                        time={new Date(
                          booking.bookingDate
                        ).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          timeZone: bookingTimezone,
                        })}
                        title={booking.serviceName}
                        client={`${booking.customerFirstName} ${booking.customerLastName}`}
                      />
                    ))
                  )}
                </div>

                <Link
                  to="/booking/bookings"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-strivre-burgundy hover:text-red-900"
                >
                  View all bookings
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Form Performance & Action Required */}
        {(hasForms || hasBooking || hasBilling) && (
          <div className="mb-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Form Performance */}
            {hasForms && (
              <div
                className={
                  hasBooking || hasBilling
                    ? "lg:col-span-2"
                    : "lg:col-span-3"
                }
              >
                <div className="h-full rounded-lg border border-strivre-lightnavy bg-white p-6">
                  <div className="mb-6 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-strivre-burgundy" />

                      <h2 className="text-lg font-bold text-strivre-navy">
                        Form Performance
                      </h2>
                    </div>

                    <Link
                      to="/forms/dashboard"
                      className="text-xs font-semibold text-strivre-burgundy hover:underline"
                    >
                      View dashboard
                    </Link>
                  </div>

                  {submissionsByForm.length === 0 ? (
                    <div className="py-8 text-center">
                      <Users
                        size={32}
                        className="mx-auto mb-3 text-strivre-lightnavy"
                      />

                      <p className="text-sm font-medium text-strivre-mutedtext">
                        No form submissions yet.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {submissionsByForm
                        .slice(0, 5)
                        .map((item) => (
                          <Link
                            key={item.formId}
                            to={`/forms/${item.formId}/submissions`}
                            className="-mx-2 block rounded-lg p-2 transition-colors hover:bg-strivre-softgrey"
                          >
                            <div className="mb-2 flex items-center justify-between">
                              <span className="text-sm font-medium text-strivre-darktext">
                                {item.formName}
                              </span>

                              <span className="text-lg font-bold text-strivre-burgundy">
                                {item.count}
                              </span>
                            </div>

                            <div className="h-2 w-full rounded-full bg-strivre-softgrey">
                              <div
                                className="h-2 rounded-full bg-strivre-burgundy transition-all"
                                style={{
                                  width: `${Math.max(
                                    (item.count /
                                      maxSubmissions) *
                                      100,
                                    item.count > 0 ? 4 : 0
                                  )}%`,
                                }}
                              />
                            </div>
                          </Link>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Required */}
            {(hasBilling || hasBooking) && (
              <div
                className={
                  hasForms
                    ? "lg:col-span-1"
                    : "lg:col-span-3"
                }
              >
                <div className="h-full rounded-lg border border-strivre-lightnavy bg-white p-6">
                  <div className="mb-6 flex items-center gap-2">
                    <Activity className="h-5 w-5 text-strivre-burgundy" />

                    <h2 className="text-lg font-bold text-strivre-navy">
                      Action Required
                    </h2>
                  </div>

                  <div
                    className={
                      hasForms
                        ? "space-y-3"
                        : "grid grid-cols-1 gap-3 md:grid-cols-2"
                    }
                  >
                    {/* Overdue Invoices */}
                    {hasBilling && (
                      <Link
                        to="/invoices"
                        className="flex items-center justify-between gap-4 rounded-lg border border-strivre-lightnavy bg-strivre-softgrey p-4 transition-all hover:border-strivre-burgundy hover:shadow-sm"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-50">
                            <CreditCard className="h-5 w-5 text-red-600" />
                          </div>

                          <div className="text-left">
                            <p className="text-sm font-semibold text-strivre-darktext">
                              Overdue Invoices
                            </p>

                            <p className="text-xs text-strivre-mutedtext">
                              Require payment follow-up
                            </p>
                          </div>
                        </div>

                        <span
                          className={`text-xl font-bold ${
                            overdueInvoices.length > 0
                              ? "text-red-600"
                              : "text-strivre-mutedtext"
                          }`}
                        >
                          {overdueInvoices.length}
                        </span>
                      </Link>
                    )}

                    {/* Pending Bookings */}
                    {hasBooking && (
                      <Link
                        to="/booking/bookings"
                        className="flex items-center justify-between gap-4 rounded-lg border border-strivre-lightnavy bg-strivre-softgrey p-4 transition-all hover:border-strivre-burgundy hover:shadow-sm"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-strivre-lightburgundy">
                            <CalendarDays className="h-5 w-5 text-strivre-burgundy" />
                          </div>

                          <div className="text-left">
                            <p className="text-sm font-semibold text-strivre-darktext">
                              Pending Bookings
                            </p>

                            <p className="text-xs text-strivre-mutedtext">
                              Awaiting confirmation
                            </p>
                          </div>
                        </div>

                        <span
                          className={`text-xl font-bold ${
                            pendingBookings.length > 0
                              ? "text-strivre-burgundy"
                              : "text-strivre-mutedtext"
                          }`}
                        >
                          {pendingBookings.length}
                        </span>
                      </Link>
                    )}
                  </div>

                  {(!hasBilling ||
                    overdueInvoices.length === 0) &&
                    (!hasBooking ||
                      pendingBookings.length === 0) && (
                      <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-center">
                        <p className="text-xs font-semibold text-emerald-700">
                          You're all caught up
                        </p>
                      </div>
                    )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Module Shortcuts */}
        {visibleModules.length > 0 && (
          <div>
            <h2 className="mb-6 text-xl font-bold text-strivre-navy">
              Platform Modules
            </h2>

            <div
              className={`grid grid-cols-1 gap-6 md:grid-cols-2 ${moduleGridClass}`}
            >
              {visibleModules.map((module) => (
                <ModuleCard
                  key={module.path}
                  icon={module.icon}
                  name={module.name}
                  description={module.description}
                  path={module.path}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}